import { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { selectAddress } from "../address/addressSlice";
import BusinessData from "../address/BusinessData";
import { ResultBlockType } from "../../types";

const itemsPerPage = 10;

function featureToBusinessRow(feature: { properties?: Record<string, unknown> }) {
  const p = feature.properties ?? {};
  const ul = (p.uniteLegale as Record<string, string | undefined> | undefined) ?? {};
  const siren = (ul.siren as string | undefined) ?? (p.siren as string | undefined) ?? "";
  const rawNaf = p.nafCode;
  const nafCode =
    rawNaf && typeof rawNaf === "object"
      ? { ...(rawNaf as Record<string, unknown>) }
      : { label: "" };
  return {
    uniteLegale: {
      denominationUniteLegale: ul.denominationUniteLegale,
      nomUniteLegale: ul.nomUniteLegale,
      siren,
    },
    etablissement: {
      activitePrincipaleEtablissement: (p.activitePrincipaleEtablissement as string) ?? "",
    },
    nafCode,
  };
}

export default function BusinessAddressAtPlaceResultBlock(_props: ResultBlockType) {
  const { t } = useTranslation();
  const address = useAppSelector(selectAddress);
  const [page, setPage] = useState(1);
  const placeId = address.value?.placeId;

  useEffect(() => {
    setPage(1);
  }, [placeId]);

  const fc = address.value?.businessAddressAtPlace;
  const allRows = useMemo(() => {
    if (!fc?.features?.length) {
      return [];
    }
    return fc.features.map(featureToBusinessRow);
  }, [fc]);

  const totalCount = allRows.length;
  const pageSlice = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return allRows.slice(start, start + itemsPerPage);
  }, [allRows, page]);

  const businessesState = useMemo(
    () => ({
      status: address.status === "loading" ? "loading" : "idle",
      value:
        address.status === "loading"
          ? undefined
          : { businesses: pageSlice, totalCount },
    }),
    [address.status, pageSlice, totalCount]
  );

  if (!address.value && address.status !== "loading") {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("businessAddressAtPlace.selectAddress")}
      </Typography>
    );
  }

  if (address.status === "idle" && address.value && fc === null) {
    return (
      <Typography variant="body2" color="error">
        {t("businessAddressAtPlace.loadError")}
      </Typography>
    );
  }

  if (address.status === "idle" && fc && totalCount === 0) {
    return (
      <Typography variant="body2" color="error" role="alert">
        {t("businessAddressAtPlace.noActivity")}
      </Typography>
    );
  }

  return (
    <BusinessData
      businesses={businessesState}
      loading={address.status === "loading"}
      itemsPerPage={itemsPerPage}
      onPageChange={(p: number) => setPage(p)}
      hideDistance
    />
  );
}
